import { useCallback, useRef } from 'react';

interface UseLongPressOptions {
  delay?: number;
  moveThreshold?: number;
  onRelease?: () => void;
}

export function useLongPress(callback: () => void, options: UseLongPressOptions = {}) {
  const { delay = 500, moveThreshold = 8, onRelease } = options;

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const onReleaseRef = useRef(onRelease);
  onReleaseRef.current = onRelease;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      firedRef.current = false;
      startPosRef.current = { x: e.clientX, y: e.clientY };
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        callbackRef.current();
      }, delay);
    },
    [delay],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const dx = Math.abs(e.clientX - startPosRef.current.x);
      const dy = Math.abs(e.clientY - startPosRef.current.y);
      if (dx > moveThreshold || dy > moveThreshold) cancel();
    },
    [cancel, moveThreshold],
  );

  const onPointerUp = useCallback(() => {
    const wasFired = firedRef.current;
    cancel();
    if (wasFired) onReleaseRef.current?.();
  }, [cancel]);

  const onPointerLeave = useCallback(() => cancel(), [cancel]);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerLeave, firedRef };
}
